import { logger } from './paycentral.helper.logging.js';

/**
 * Parses a string or number into a number, handling locale-specific formats.
 * @param {string|number|null|undefined} value - The value to parse (e.g., "1,234.56", "1.234,56", 1234.56).
 * @returns {number|null} Parsed number or null if invalid or empty.
 */
export function parseAmount(value) {
    if (value == null || value === '') {
        logger.logVerboseInfo(`parseAmount: Received null or empty value: ${value}`);
        return null;
    }

    let parsed;
    if (typeof value === 'number') {
        parsed = value;
    } else if (typeof value === 'string') {
        parsed = window.Asi?.Localization?.Number.parseLocaleString(value) ?? NaN;
    } else {
        parsed = NaN;
    }

    if (!isValidAmount(parsed)) {
        logger.logVerboseInfo(`parseAmount: Invalid parsed value: ${parsed} from input: ${value}`);
        return null;
    }
    // Ensure returned amount is limited to two decimal places.
    return Math.round(parsed * 100) / 100;
}

/**
 * Formats a number to a locale-specific two-decimal string.
 * @param {number|null|undefined} value - The value to format (e.g., 1234.56).
 * @returns {string} A localized string with two decimal places (e.g., "1,234.56") or "0.00" if invalid.
 */
export function formatAmount(value) {
    if (!isValidAmount(value)) {
        logger.logVerboseInfo(`formatAmount: Invalid or null value: ${value}, defaulting to "0.00"`);
        return '0.00';
    }

    const culture = window.Asi?.Localization?.getCurrentCulture() || 'en-US';
    return new Intl.NumberFormat(culture, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true
    }).format(value);
}

/**
 * Validates a value as a non-negative number.
 * @param {any} value - The value to validate.
 * @returns {boolean} True if valid (number, non-negative), false otherwise.
 */
export function isValidAmount(value) {
    return typeof value === 'number' && !isNaN(value) && value >= 0;
}